import { reportInfo } from '@ms/yammer-telemetry';
import type { Authenticator } from '@ms/yammer-web-authenticators';
import { AuthenticatorType, VivaConnectionsAuthenticator, setAuthenticator } from '@ms/yammer-web-authenticators';
import { TeamsAuthenticatorV2 } from '@ms/yammer-web-authenticators/dist/teams';
import { getNetworkContext } from '@ms/yammer-web-support/dist/location';
import {
  getMetaOsHubContextThrows,
  isInMetaOsHub,
  isNAAChannelRecommendedWithTelemetry,
} from '@ms/yammer-web-support/dist/metaOsHub';
import { isMobileEmbedView } from '@ms/yammer-web-support/dist/userAgent';
import config from 'bootstrap/config';
import { ensureVivaConnectionsInitialized, getAuthToken } from 'mobileEmbedView';

import { applyNaaClientIdOverride } from './applyNaaClientIdOverride';
import { getAuthTokenCache, initializeAuthTokenCache } from './authTokenCache';
import { reportAuthError } from './reportAuthError';

type GetAuthenticator = () => Promise<Authenticator>;
const getAuthenticator: GetAuthenticator = async () => {
  if (await isInMetaOsHub()) {
    initializeAuthTokenCache();

    const isNAASupported = await isNAAChannelRecommendedWithTelemetry();
    if (isNAASupported) {
      reportAuthenticatorType(AuthenticatorType.NestedAppAuth);

      const context = await getMetaOsHubContextThrows();
      const tenant = config.adal.tenant;

      await applyNaaClientIdOverride(context.app.host.name);

      // oxlint-disable-next-line typescript/no-explicit-any
      (config as any).adal = config.msal;

      const authenticatorOptions = {
        getAuthTokenCache,
        instance: config.adal.instance,
        clientId: config.adal.clientId,
        tenant,
        onError: reportAuthError,
        accountFilter: {
          homeAccountId: context.user?.id,
          tenantId: context.user?.tenant?.id,
          loginHint: context.user?.loginHint,
        },
      };

      return new TeamsAuthenticatorV2(authenticatorOptions);
    }

    const teamsAuthenticatorPromise = import(
      /* webpackChunkName: "4-auth-teams" */ '@ms/yammer-web-authenticators/dist/teams'
    );
    reportAuthenticatorType(AuthenticatorType.Teams);

    const { TeamsAuthenticator } = await teamsAuthenticatorPromise;

    return new TeamsAuthenticator(getAuthTokenCache);
  }

  if (isMobileEmbedView()) {
    ensureVivaConnectionsInitialized();
    reportAuthenticatorType(AuthenticatorType.VivaConnections);

    return new VivaConnectionsAuthenticator(getAuthToken);
  }

  const networkContext = getNetworkContext(window.location.pathname);
  const tenant = networkContext?.networkType === 'Canonical' ? networkContext.permalink : config.msal.tenant;

  // oxlint-disable-next-line typescript/no-explicit-any
  (config as any).adal = config.msal;

  const { default: MsalRedirectAuthenticator } = await import(
    /* webpackChunkName: "4-auth-msal" */ '@ms/yammer-web-authenticators/dist/redirect/MsalRedirectAuthenticator'
  );
  reportAuthenticatorType(AuthenticatorType.MsalRedirect);

  return new MsalRedirectAuthenticator({
    ...config.msal,
    tenant,
  });
};

type SetupAuthenticator = () => Promise<Authenticator>;
export const setupAuthenticator: SetupAuthenticator = async () => {
  const authenticator = await getAuthenticator();
  setAuthenticator(authenticator);

  return authenticator;
};

export const reportAuthenticatorType = (authenticatorType: AuthenticatorType): void => {
  reportInfo({
    eventName: 'teams_authenticator_info',
    eventProperties: {
      authenticatorType,
    },
  });
};
